import {
  Attributes,
  AttributesRow,
  ButtonGroup,
  Card,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Link, VStack, Show} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {equals, isNil} from 'ramda';
import {isEmptyString} from 'ramda-adjunct';

import {
  useGetCustomerV2Query,
  useGetEmployeeMechanicsQuery,
  useGetUserQuery,
} from '@dms/api/customer';
import {useGetVehicleQuery} from '@dms/api/sales';
import {
  useGetDirectSaleQuery,
  useGetFastTrackIssueQuery,
  usePatchFastTrackIssueMutation,
} from '@dms/api/warehouse';
import {useGetMetadaServiceCaseQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {customerRoutes, warehouseRoutes, workshopRoutes, vehiclesRoutes} from '@dms/routes';
import {getCustomerName, getUserName, handleApiError, logError, queryParams} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {
  composePath,
  EMPTY_PLACEHOLDER,
  Nullish,
  parseDate,
  RequiredTestIdProps,
  suffixTestId,
} from 'shared/web';

import {
  GeneralInformationTabFormDefinition,
  GeneralInformationTabFormDefinitionSchema,
} from '../types/GeneralInformationTabFormDefinition';

interface GeneralInformationTabProps extends RequiredTestIdProps {}

export function GeneralInformationTab(props: GeneralInformationTabProps) {
  const {fastTrackIssueId} = useWarehouseParams();

  const formatDate = useDateTimeFormatter();

  const fastTrackQuery = useGetFastTrackIssueQuery(
    {fastTrackId: fastTrackIssueId},
    {skip: isNil(fastTrackIssueId) || isEmptyString(fastTrackIssueId)}
  );

  const serviceCaseId = fastTrackQuery.data?.originEntityHeader?.serviceOrder?.serviceCaseId;
  const directSaleId = fastTrackQuery.data?.originEntityHeader?.directSale?.directSaleId;

  const {data: serviceCase} = useGetMetadaServiceCaseQuery(
    {serviceCaseId: serviceCaseId as string},
    {skip: isNil(serviceCaseId) || isNil(fastTrackQuery.data?.receivedBy)}
  );

  const directSaleQuery = useGetDirectSaleQuery(
    {directSaleId: directSaleId as string},
    {skip: isNil(directSaleId) || isNil(fastTrackQuery.data?.receivedBy)}
  );

  const authorizationProfileId = isNil(serviceCaseId)
    ? directSaleQuery.data?.authorizationProfileId
    : serviceCase?.authorizationProfileId;

  const mechanicsQuery = useGetEmployeeMechanicsQuery(
    {
      authorizationProfileId: authorizationProfileId as string,
      isActive: true,
      withToOffer: true,
    },
    {
      skip:
        isNil(authorizationProfileId) ||
        isEmptyString(authorizationProfileId) ||
        isNil(fastTrackQuery.data?.receivedBy),
    }
  );

  const receivedByMechanic = mechanicsQuery.data?.find(
    (mechanic) => mechanic?.employeeId === fastTrackQuery.data?.receivedBy
  );

  const [patchFastTrackIssue, {isLoading: isSavingChanges}] = usePatchFastTrackIssueMutation();

  const createdByUserQuery = useGetUserQuery(
    {
      id: fastTrackQuery.data?.createdBy as string,
    },
    {skip: isNil(fastTrackQuery.data?.createdBy) || isEmptyString(fastTrackQuery.data?.createdBy)}
  );

  const fastTrackIssuedByUserQuery = useGetUserQuery(
    {
      id: fastTrackQuery.data?.fastTrackIssuedBy as string,
    },
    {
      skip:
        isNil(fastTrackQuery.data?.fastTrackIssuedBy) ||
        isEmptyString(fastTrackQuery.data?.fastTrackIssuedBy),
    }
  );

  const completedByUserQuery = useGetUserQuery(
    {
      id: fastTrackQuery.data?.completedBy as string,
    },
    {
      skip:
        isNil(fastTrackQuery.data?.completedBy) || isEmptyString(fastTrackQuery.data?.completedBy),
    }
  );

  const directSaleCustomerQuery = useGetCustomerV2Query(
    {
      customerId: fastTrackQuery.data?.originEntityHeader?.directSale?.customerId as string,
    },
    {
      skip:
        isNil(fastTrackQuery.data?.originEntityHeader?.directSale?.customerId) ||
        isEmptyString(fastTrackQuery.data?.originEntityHeader?.directSale?.customerId),
    }
  );

  const serviceOrderCustomerQuery = useGetCustomerV2Query(
    {
      customerId: fastTrackQuery.data?.originEntityHeader?.serviceOrder?.customerId as string,
    },
    {
      skip:
        isNil(fastTrackQuery.data?.originEntityHeader?.serviceOrder?.customerId) ||
        isEmptyString(fastTrackQuery.data?.originEntityHeader?.serviceOrder?.customerId),
    }
  );

  const directSaleVehicleQuery = useGetVehicleQuery(
    {
      vehicleId: fastTrackQuery.data?.originEntityHeader?.directSale?.vehicleId as string,
    },
    {
      skip:
        isNil(fastTrackQuery.data?.originEntityHeader?.directSale?.vehicleId) ||
        isEmptyString(fastTrackQuery.data?.originEntityHeader?.directSale?.vehicleId),
    }
  );

  const serviceOrderVehicleQuery = useGetVehicleQuery(
    {
      vehicleId: fastTrackQuery.data?.originEntityHeader?.serviceOrder?.vehicleId as string,
    },
    {
      skip:
        isNil(fastTrackQuery.data?.originEntityHeader?.serviceOrder?.vehicleId) ||
        isEmptyString(fastTrackQuery.data?.originEntityHeader?.serviceOrder?.vehicleId),
    }
  );

  const handleSubmit: FormSubmitHandler<GeneralInformationTabFormDefinition> = async (
    formValues
  ) => {
    if (equals(formValues, defaultValues)) {
      return;
    }

    if (isNil(fastTrackIssueId) || isEmptyString(fastTrackIssueId)) {
      return logError('fastTrackIssueId is not defined');
    }

    await patchFastTrackIssue({
      fastTrackId: fastTrackIssueId,
      body: {
        note: formValues.note,
      },
    })
      .unwrap()
      .catch(handleApiError);
  };

  const formatToShortDateTime = (date: string | Nullish) => {
    if (isNil(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return formatDate('dateTimeShort', parseDate(date));
  };

  const basicInformation: AttributesRow[] = [
    {
      label: i18n.t('general.labels.createdBy'),
      value: getUserName(createdByUserQuery.data),
    },
    {
      label: i18n.t('general.labels.createdAt'),
      value: formatToShortDateTime(fastTrackQuery.data?.createdAt),
    },
    {
      label: i18n.t('entity.warehouse.labels.fastTrackIssuedBy'),
      value: getUserName(fastTrackIssuedByUserQuery.data),
    },
    {
      label: i18n.t('entity.warehouse.labels.fastTrackIssuedAt'),
      value: formatToShortDateTime(fastTrackQuery.data?.fastTrackIssuedAt),
    },
    {
      label: i18n.t('general.labels.receivedBy'),
      value: receivedByMechanic?.name,
    },
    {
      label: i18n.t('general.labels.receivedAt'),
      value: formatToShortDateTime(fastTrackQuery.data?.receivedAt),
    },
    {
      label: i18n.t('general.labels.completedBy'),
      value: getUserName(completedByUserQuery.data),
    },
    {
      label: i18n.t('general.labels.completedAt'),
      value: formatToShortDateTime(fastTrackQuery.data?.completedAt),
    },
  ];

  const directSaleInformation: AttributesRow[] = [
    {
      label: i18n.t('general.labels.number'),
      content: fastTrackQuery.data?.originEntityHeader?.directSale?.number ? (
        <Link
          title={fastTrackQuery.data?.originEntityHeader?.directSale?.number}
          rightIcon="action/open_in_new"
          size="small"
          href={composePath(warehouseRoutes.directSalesDetailOverview, {
            params: {id: fastTrackQuery.data?.originEntityHeader?.directSale?.directSaleId},
            isSearchParamsPreserved: false,
          })}
          target="_blank"
        />
      ) : (
        EMPTY_PLACEHOLDER
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.orderType'),
      value: fastTrackQuery.data?.originEntityHeader?.directSale?.type.name,
    },
    {
      label: i18n.t('general.labels.variant'),
      value: fastTrackQuery.data?.originEntityHeader?.directSale?.variant.name,
    },
    {
      label: i18n.t('general.labels.customer'),
      content: directSaleCustomerQuery.data ? (
        <Link
          title={getCustomerName(directSaleCustomerQuery.data)}
          rightIcon="action/open_in_new"
          size="small"
          href={composePath(customerRoutes.detail, {
            params: {id: directSaleCustomerQuery.data?.id},
            isSearchParamsPreserved: false,
          })}
          target="_blank"
        />
      ) : (
        EMPTY_PLACEHOLDER
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.vehicle'),
      content: directSaleVehicleQuery.data ? (
        <Link
          title={directSaleVehicleQuery.data?.title}
          rightIcon="action/open_in_new"
          size="small"
          href={composePath(vehiclesRoutes.detail, {
            params: {id: directSaleVehicleQuery.data?.id},
            isSearchParamsPreserved: false,
          })}
          target="_blank"
        />
      ) : (
        EMPTY_PLACEHOLDER
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.licensePlate'),
      value: directSaleVehicleQuery.data?.state?.registrationPlate,
    },
    {
      label: i18n.t('entity.warehouse.labels.vin'),
      value: directSaleVehicleQuery.data?.vin,
    },
  ];

  const serviceOrderInformation: AttributesRow[] = [
    {
      label: i18n.t('general.labels.number'),
      content: fastTrackQuery.data?.originEntityHeader?.serviceOrder?.number ? (
        <Link
          title={fastTrackQuery.data?.originEntityHeader?.serviceOrder?.number}
          rightIcon="action/open_in_new"
          size="small"
          href={composePath(workshopRoutes.serviceCaseDetail, {
            params: {id: fastTrackQuery.data?.originEntityHeader?.serviceOrder?.serviceCaseId},
            queryParams: {
              [queryParams.SERVICE_CASE_ORDER_ID]:
                fastTrackQuery.data?.originEntityHeader?.serviceOrder?.serviceOrderId,
            },
            isSearchParamsPreserved: false,
          })}
          target="_blank"
        />
      ) : (
        EMPTY_PLACEHOLDER
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.orderType'),
      value: fastTrackQuery.data?.originEntityHeader?.serviceOrder?.type.name,
    },
    {
      label: i18n.t('general.labels.variant'),
      value: fastTrackQuery.data?.originEntityHeader?.serviceOrder?.variant.name,
    },
    {
      label: i18n.t('general.labels.customer'),
      content: serviceOrderCustomerQuery.data ? (
        <Link
          title={getCustomerName(serviceOrderCustomerQuery.data)}
          rightIcon="action/open_in_new"
          size="small"
          href={composePath(customerRoutes.detail, {
            params: {id: serviceOrderCustomerQuery.data?.id},
            isSearchParamsPreserved: false,
          })}
          target="_blank"
        />
      ) : (
        EMPTY_PLACEHOLDER
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.vehicle'),
      content: serviceOrderVehicleQuery.data ? (
        <Link
          title={serviceOrderVehicleQuery.data?.title}
          rightIcon="action/open_in_new"
          size="small"
          href={composePath(vehiclesRoutes.detail, {
            params: {id: serviceOrderVehicleQuery.data?.id},
            isSearchParamsPreserved: false,
          })}
          target="_blank"
        />
      ) : (
        EMPTY_PLACEHOLDER
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.licensePlate'),
      value: serviceOrderVehicleQuery.data?.state?.registrationPlate,
    },
    {
      label: i18n.t('entity.warehouse.labels.vin'),
      value: serviceOrderVehicleQuery.data?.vin,
    },
  ];

  const defaultValues: GeneralInformationTabFormDefinition = {
    note: fastTrackQuery.data?.note,
  };

  return (
    <DataStatus
      isLoading={fastTrackQuery.isLoading}
      isError={fastTrackQuery.isError}
      minHeight="100%"
    >
      <Card
        title={i18n.t('general.labels.basicInformation')}
        hasSeparator
        data-testid={suffixTestId('wrapper', props)}
      >
        <VStack spacing={4}>
          <Attributes size="quarter" rows={basicInformation} />
          <Show when={fastTrackQuery.data?.originEntityHeader?.directSale}>
            <Card title={i18n.t('entity.warehouse.labels.directSale')} variant="inlineGrey">
              <Attributes size="quarter" rows={directSaleInformation} />
            </Card>
          </Show>
          <Show when={fastTrackQuery.data?.originEntityHeader?.serviceOrder}>
            <Card title={i18n.t('entity.warehouse.labels.serviceOrder')} variant="inlineGrey">
              <Attributes size="quarter" rows={serviceOrderInformation} />
            </Card>
          </Show>
          <Form<GeneralInformationTabFormDefinition>
            defaultValues={defaultValues}
            experimentalZodSchema={GeneralInformationTabFormDefinitionSchema}
            onSubmit={handleSubmit}
          >
            {(control, formApi) => (
              <VStack spacing={4}>
                <FormField
                  control={control}
                  name="note"
                  type="textarea"
                  label={i18n.t('general.labels.note')}
                  data-testid={suffixTestId('note', props)}
                />
                <ButtonGroup align="right">
                  <FormButton
                    isLoading={isSavingChanges}
                    control={control}
                    type="button"
                    variant="secondary"
                    title={i18n.t('general.actions.discardChanges')}
                    onClick={() => formApi.reset(defaultValues)}
                    data-testid={suffixTestId('actions.discardChanges', props)}
                  />
                  <FormButton
                    isLoading={isSavingChanges}
                    control={control}
                    type="submit"
                    variant="primary"
                    title={i18n.t('general.actions.saveChanges')}
                    data-testid={suffixTestId('actions.saveChanges', props)}
                  />
                </ButtonGroup>
              </VStack>
            )}
          </Form>
        </VStack>
      </Card>
    </DataStatus>
  );
}
